import React from "react";
//import arrayDestruct from "../assets/portfolio/arrayDestruct.jpg";
import installNode from "../assets/portfolio/installNode.jpg";
import navbar from "../assets/portfolio/navbar.jpg";
import reactParallax from "../assets/portfolio/reactParallax.jpg";
import reactSmooth from "../assets/portfolio/reactSmooth.jpg";
import reactWeather from "../assets/portfolio/reactWeather.jpg";
import todo_NextJs from "../assets/portfolio/todo_NextJs.png";
import { SparklesCore } from "./ui/sparkles";
import { PortfolioCarousel } from "./ui/portfolio-carousel";
import { Bot, Brain, Layers, Route, Workflow } from "lucide-react";

// Dummy links for now — replace with real repo/demo URLs when published
const DUMMY_LINK = "https://github.com/fahimar";

const Portfolio = () => {
  const aiProjects = [
    {
      id: 1,
      title: "RAG Knowledge Assistant",
      description:
        "Retrieval-Augmented Generation chatbot that answers questions over custom documents using vector search and grounded LLM responses.",
      tags: ["LangChain", "FAISS", "FastAPI"],
      icon: <Brain className="w-6 h-6 text-white" />,
      gradient: "from-cyan-500 to-blue-600",
      demoLink: DUMMY_LINK,
      codeLink: DUMMY_LINK,
    },
    {
      id: 2,
      title: "Multi-Query RAG Pipeline",
      description:
        "Expands a single user question into multiple queries, retrieves in parallel, and re-ranks results for higher answer accuracy.",
      tags: ["LangChain", "OpenAI", "Vector DB"],
      icon: <Layers className="w-6 h-6 text-white" />,
      gradient: "from-blue-500 to-indigo-600",
      demoLink: DUMMY_LINK,
      codeLink: DUMMY_LINK,
    },
    {
      id: 3,
      title: "Adaptive RAG System",
      description:
        "Routes each query by complexity — direct answer, retrieval, or web search — with self-corrective grading of retrieved context.",
      tags: ["LangGraph", "Groq", "Python"],
      icon: <Route className="w-6 h-6 text-white" />,
      gradient: "from-indigo-500 to-purple-600",
      demoLink: DUMMY_LINK,
      codeLink: DUMMY_LINK,
    },
    {
      id: 4,
      title: "LLM Support Chatbot",
      description:
        "History-aware customer support chatbot with intent interception, conversation memory, and usage tracking in production.",
      tags: ["Llama 3", "Redis", "PostgreSQL"],
      icon: <Bot className="w-6 h-6 text-white" />,
      gradient: "from-purple-500 to-pink-600",
      demoLink: DUMMY_LINK,
      codeLink: DUMMY_LINK,
    },
    {
      id: 5,
      title: "AI Workflow Automation",
      description:
        "Automated business workflows powered by LLM agents — lead handling, content generation, and data extraction end-to-end.",
      tags: ["AI Agents", "n8n", "OpenAI API"],
      icon: <Workflow className="w-6 h-6 text-white" />,
      gradient: "from-pink-500 to-rose-600",
      demoLink: DUMMY_LINK,
      codeLink: DUMMY_LINK,
    },
  ];

  const webProjects = [
    {
      id: 1,
      src: todo_NextJs,
      title: "Next.js Todo App",
      description: "Task manager built with Next.js during Learnathon 2.0",
      codeLink:
        "https://github.com/fahimar/learnathon-2-NextJS/tree/main/class-1/todo_list-app",
    },
    {
      id: 2,
      src: reactParallax,
      title: "React Parallax Website",
      description: "Landing page with smooth parallax scroll effects",
    },
    {
      id: 3,
      src: navbar,
      title: "Responsive Navbar",
      description: "Mobile-first navigation with animated menu",
    },
    {
      id: 4,
      src: reactSmooth,
      title: "Smooth Scroll UI",
      description: "Scroll-driven UI transitions in React",
    },
    {
      id: 5,
      src: installNode,
      title: "Node.js Setup Guide",
      description: "Environment setup walkthrough for Node.js",
    },
    {
      id: 6,
      src: reactWeather,
      title: "Weather App",
      description: "Live weather forecasts from a public API",
      codeLink: "https://github.com/fahimar/Weathering_With_You_",
    },
  ];

  const handleCodeButtonClick = (codeLink) => {
    window.location.href = codeLink;
  };

  return (
    <div
      name="portfolio"
      className="bg-gradient-to-b from-black via-gray-900 to-black w-full min-h-screen text-white py-20 relative"
    >
      {/* Sparkles Background Animation */}
      <div className="z-0 w-full absolute inset-0 h-full">
        <SparklesCore
          id="tsparticlesportfolio"
          background="transparent"
          minSize={0.6}
          maxSize={1.4}
          particleDensity={80}
          className="w-full h-full"
          particleColor="#FFFFFF"
          speed={0.5}
        />
      </div>
      <div
        className="max-w-screen-lg p-4 mx-auto flex flex-col
      justify-center w-full h-full relative z-10"
      >
        {/* Section Title */}
        <div className="pb-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold inline bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 bg-clip-text text-transparent">
            Portfolio
          </h2>
          <div className="mt-4 mx-auto w-24 h-1 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full"></div>
        </div>

        {/* Subtitle */}
        <div className="text-center mb-12">
          <p className="text-lg sm:text-xl text-gray-300 font-medium">
            AI-native engineering — from RAG pipelines to production web apps
          </p>
        </div>

        {/* AI & LLM Engineering */}
        <div className="mb-16">
          <div className="mb-8">
            <h3 className="text-2xl sm:text-3xl font-bold text-white">
              AI & LLM Engineering
            </h3>
            <p className="text-gray-400 mt-2">
              RAG systems, LLM applications & AI automation
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {aiProjects.map(
              ({
                id,
                title,
                description,
                tags,
                icon,
                gradient,
                demoLink,
                codeLink,
              }) => (
                <div
                  key={id}
                  className="group relative flex flex-col rounded-2xl border border-gray-700/50 bg-gray-900/60 backdrop-blur-sm p-6 transition-all duration-300 hover:-translate-y-2 hover:border-cyan-500/50 hover:shadow-lg hover:shadow-cyan-500/20"
                >
                  <div
                    className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center mb-4 shadow-lg`}
                  >
                    {icon}
                  </div>
                  <h4 className="text-lg font-bold text-white">{title}</h4>
                  <p className="text-sm text-gray-400 mt-2 flex-1">
                    {description}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs px-2.5 py-1 rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/20"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mt-5">
                    <a
                      href={demoLink}
                      target="_blank"
                      rel="noreferrer"
                      className="flex-1 text-center px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-xs font-medium hover:bg-white/20 transition-all duration-300"
                    >
                      Demo
                    </a>
                    <a
                      href={codeLink}
                      target="_blank"
                      rel="noreferrer"
                      className="flex-1 text-center px-3 py-2 bg-cyan-500/80 border border-cyan-400/30 rounded-lg text-white text-xs font-medium hover:bg-cyan-500 transition-all duration-300"
                    >
                      Code
                    </a>
                  </div>
                </div>
              )
            )}
          </div>
        </div>

        {/* Web Development */}
        <div>
          <div className="mb-8">
            <h3 className="text-2xl sm:text-3xl font-bold text-white">
              Web Development
            </h3>
            <p className="text-gray-400 mt-2">
              Production web apps & UI experiments
            </p>
          </div>

          <PortfolioCarousel
            items={webProjects}
            onCodeClick={handleCodeButtonClick}
          />
        </div>
      </div>
    </div>
  );
};

export default Portfolio;
